import {
  normalizeGroupTalkMemberId,
  normalizeGroupTalkStanceId,
  type GroupTalkDraftMode,
  type GroupTalkParticipant,
  type GroupTalkTemplateId,
} from "./groupTalk";
import type {
  KnowledgeAiPreparationStatus,
  KnowledgeFailureReason,
  KnowledgeItemStatus,
} from "./api";
import { getKnowledgeItemDisplayName } from "./knowledge";
import type { GroupTalkSessionView, GroupTalkTurnView } from "./groupTalkView";

const GROUP_TALK_DRAFT_KEY = "wizmate.group-talk.draft.v1";
const GROUP_TALK_ACTIVE_SESSION_KEY_PREFIX = "wizmate.group-talk.session.v1:";

export type GroupTalkDraft = {
  templateId: GroupTalkTemplateId;
  title: string;
  description: string;
  participants: GroupTalkParticipant[];
  mode?: GroupTalkDraftMode;
  consultationText?: string;
  recommendationReason?: string;
  recommendationConfidence?: string;
  memberEditingLocked?: boolean;
  knowledgeAttachment?: GroupTalkKnowledgeAttachmentDraft | null;
};

export type GroupTalkKnowledgeAttachmentType = "pdf" | "executive_memo" | "url";

export type GroupTalkKnowledgeAttachmentDraft = {
  id: string;
  type?: GroupTalkKnowledgeAttachmentType;
  title: string;
  originalFilename?: string | null;
  status?: KnowledgeItemStatus;
  failureReason?: KnowledgeFailureReason | string | null;
  pageCount?: number | null;
  textExtractable?: boolean | null;
  aiProcessingAllowed?: boolean | null;
  aiPreparationStatus?: KnowledgeAiPreparationStatus | null;
  hasSummary?: boolean;
  summarizedAt?: string | null;
};

export function isGroupTalkKnowledgeAttachmentDraftUsable(
  item: GroupTalkKnowledgeAttachmentDraft | null | undefined
): boolean {
  if (!item) return false;
  const type = item.type ?? "pdf";
  if (type !== "pdf" && type !== "executive_memo" && type !== "url") return false;
  if ((item.status ?? "ready") !== "ready" || item.failureReason) return false;

  if (type === "url") {
    return item.textExtractable === true
      && item.aiProcessingAllowed === true
      && (item.aiPreparationStatus === "ready" || item.aiPreparationStatus == null);
  }

  const textExtractable = item.textExtractable === undefined ? true : item.textExtractable === true;
  const aiProcessingAllowed = item.aiProcessingAllowed === undefined ? true : item.aiProcessingAllowed === true;
  return textExtractable && aiProcessingAllowed;
}

export type GroupTalkActiveSessionSnapshot = {
  sessionId: string;
  draft: GroupTalkDraft;
  session: GroupTalkSessionView;
  turns: GroupTalkTurnView[];
  initialUserMessage?: string;
  initialUserMessageSent?: boolean;
};

function canUseSessionStorage() {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function safeParseJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function isValidParticipant(value: unknown): value is GroupTalkParticipant {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<GroupTalkParticipant>;
  return (
    typeof item.participantId === "string" &&
    typeof item.memberId === "string" &&
    (typeof item.stanceId === "string" || item.stanceId === null) &&
    typeof item.fixed === "boolean"
  );
}

function isValidDraft(value: unknown): value is GroupTalkDraft {
  if (!value || typeof value !== "object") return false;
  const draft = value as Partial<GroupTalkDraft>;
  return (
    typeof draft.templateId === "string" &&
    typeof draft.title === "string" &&
    typeof draft.description === "string" &&
    Array.isArray(draft.participants) &&
    draft.participants.every((participant) => isValidParticipant(participant))
  );
}

function canUseLegacyReadyKnowledgeFlags(status: KnowledgeItemStatus | undefined, type: string): boolean {
  const effectiveStatus = status ?? "ready";
  return effectiveStatus === "ready" && (type === "pdf" || type === "executive_memo");
}

function hasOwnProperty(value: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function normalizeStoredKnowledgeFlag(
  item: Partial<GroupTalkKnowledgeAttachmentDraft>,
  key: "textExtractable" | "aiProcessingAllowed",
  legacyReadyFlags: boolean
): boolean | null {
  if (hasOwnProperty(item, key)) {
    const value = item[key];
    return typeof value === "boolean" ? value : null;
  }
  return legacyReadyFlags ? true : null;
}

function normalizeStoredKnowledgeAttachment(value: unknown): GroupTalkKnowledgeAttachmentDraft | null {
  if (!value || typeof value !== "object") return null;
  const item = value as Partial<GroupTalkKnowledgeAttachmentDraft>;
  if (typeof item.id !== "string" || item.id.trim() === "") return null;
  const type = typeof item.type === "string" && item.type.trim() !== "" ? item.type.trim() : "pdf";
  if (type !== "pdf" && type !== "executive_memo" && type !== "url") return null;
  const fallbackTitle = type === "executive_memo" ? "社長メモ" : type === "url" ? "Webページ" : "PDF資料";
  const originalFilename = typeof item.originalFilename === "string" ? item.originalFilename : null;
  const title = getKnowledgeItemDisplayName({
    type,
    title: typeof item.title === "string" ? item.title : fallbackTitle,
    original_filename: originalFilename,
  });
  const status = item.status;
  const legacyReadyFlags = canUseLegacyReadyKnowledgeFlags(status, type);
  return {
    id: item.id,
    type,
    title,
    originalFilename,
    status,
    failureReason: typeof item.failureReason === "string" ? item.failureReason : null,
    pageCount: typeof item.pageCount === "number" ? item.pageCount : null,
    textExtractable: normalizeStoredKnowledgeFlag(item, "textExtractable", legacyReadyFlags),
    aiProcessingAllowed: normalizeStoredKnowledgeFlag(item, "aiProcessingAllowed", legacyReadyFlags),
    aiPreparationStatus:
      item.aiPreparationStatus === "processing"
      || item.aiPreparationStatus === "ready"
      || item.aiPreparationStatus === "failed"
        ? item.aiPreparationStatus
        : null,
    hasSummary: Boolean(item.hasSummary),
    summarizedAt: typeof item.summarizedAt === "string" ? item.summarizedAt : null,
  };
}

function normalizeStoredParticipant(participant: GroupTalkParticipant): GroupTalkParticipant {
  const memberId = normalizeGroupTalkMemberId(participant.memberId);
  const { displayName: _displayName, personaKey: _personaKey, avatarKey: _avatarKey, imageSrc: _imageSrc, ...rest } = participant;
  const displayName = typeof participant.displayName === "string" && participant.displayName.trim() !== ""
    ? participant.displayName.trim()
    : undefined;
  const personaKey = typeof participant.personaKey === "string" && participant.personaKey.trim() !== ""
    ? participant.personaKey.trim()
    : undefined;
  const avatarKey = typeof participant.avatarKey === "string" && participant.avatarKey.trim() !== ""
    ? participant.avatarKey.trim()
    : undefined;
  const imageSrc = typeof participant.imageSrc === "string" && participant.imageSrc.trim() !== ""
    ? participant.imageSrc.trim()
    : undefined;
  return {
    ...rest,
    memberId,
    stanceId: memberId === "mei" ? null : normalizeGroupTalkStanceId(participant.stanceId),
    fixed: memberId === "mei" ? true : participant.fixed,
    ...(displayName ? { displayName } : {}),
    ...(personaKey ? { personaKey } : {}),
    ...(avatarKey ? { avatarKey } : {}),
    ...(imageSrc ? { imageSrc } : {}),
    imageVariant: participant.imageVariant === 2 ? 2 : participant.imageVariant === 1 ? 1 : undefined,
  };
}

function normalizeStoredDraft(draft: GroupTalkDraft): GroupTalkDraft {
  return {
    ...draft,
    mode: draft.mode === "recommend" || draft.mode === "template" || draft.mode === "custom" ? draft.mode : undefined,
    consultationText: typeof draft.consultationText === "string" ? draft.consultationText : "",
    recommendationReason: typeof draft.recommendationReason === "string" ? draft.recommendationReason : "",
    recommendationConfidence: typeof draft.recommendationConfidence === "string" ? draft.recommendationConfidence : "",
    memberEditingLocked: Boolean(draft.memberEditingLocked),
    knowledgeAttachment: normalizeStoredKnowledgeAttachment(draft.knowledgeAttachment),
    participants: draft.participants.map((participant) => normalizeStoredParticipant(participant)),
  };
}

function activeSessionKey(sessionId: string) {
  return `${GROUP_TALK_ACTIVE_SESSION_KEY_PREFIX}${sessionId}`;
}

export function loadGroupTalkDraft(): GroupTalkDraft | null {
  if (!canUseSessionStorage()) return null;
  const parsed = safeParseJson<unknown>(window.sessionStorage.getItem(GROUP_TALK_DRAFT_KEY));
  return isValidDraft(parsed) ? normalizeStoredDraft(parsed) : null;
}

export function saveGroupTalkDraft(draft: GroupTalkDraft) {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.setItem(GROUP_TALK_DRAFT_KEY, JSON.stringify(normalizeStoredDraft(draft)));
}

export function clearGroupTalkDraft() {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(GROUP_TALK_DRAFT_KEY);
}

export function loadGroupTalkActiveSessionSnapshot(sessionId: string): GroupTalkActiveSessionSnapshot | null {
  if (!canUseSessionStorage()) return null;
  return safeParseJson<GroupTalkActiveSessionSnapshot>(window.sessionStorage.getItem(activeSessionKey(sessionId)));
}

export function saveGroupTalkActiveSessionSnapshot(snapshot: GroupTalkActiveSessionSnapshot) {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.setItem(activeSessionKey(snapshot.sessionId), JSON.stringify({
    ...snapshot,
    draft: normalizeStoredDraft(snapshot.draft),
  }));
}

export function clearGroupTalkActiveSessionSnapshot(sessionId: string) {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(activeSessionKey(sessionId));
}
